// communication.service.ts
import { api } from './api';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

// Types
export interface EmailTemplate {
    id: string;
    name: string;
    category: 'outreach' | 'follow_up' | 'proposal' | 'contract' | 'invoice' | 'thank_you';
    subject: string;
    bodyHtml: string;
    variables: string[];
    teamId: string;
    createdBy: string;
    isActive: boolean;
    isBuiltIn?: boolean; // System templates that cannot be edited/deleted
    createdAt: string;
    updatedAt: string;
}

export interface EmailLog {
    id: string;
    to: string[];
    cc: string[];
    subject: string;
    bodyHtml: string;
    status: 'sent' | 'opened' | 'replied' | 'bounced' | 'scheduled';
    templateId?: string;
    template?: {
        id: string;
        name: string;
        category: string;
    };
    partnershipId?: string;
    partnership?: {
        id: string;
        brand: string;
        contact: string;
        email: string;
    };
    sentAt: string;
    openedAt?: string;
    repliedAt?: string;
    scheduledFor?: string;
    teamId: string;
    sentBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CommunicationStats {
    totalSent: number;
    totalOpened: number;
    totalReplied: number;
    openRate: number;
    replyRate: number;
    followUpsDue: number;
}

export interface CreateEmailTemplateData {
    name: string;
    category: string;
    subject: string;
    bodyHtml: string;
    variables?: string[];
}

export interface CreateEmailLogData {
    to: string[];
    cc?: string[] | undefined;
    subject: string;
    bodyHtml: string;
    templateId?: string | undefined;
    partnershipId?: string | undefined;
    scheduledFor?: string | undefined;
}

// API endpoints
const API_ENDPOINTS = {
    templates: '/communication/templates',
    template: (id: string) => `/communication/templates/${id}`,
    emails: '/communication/emails',
    email: (id: string) => `/communication/emails/${id}`,
    emailStatus: (id: string) => `/communication/emails/${id}/status`,
    stats: '/communication/stats',
};

// Type guard for error with response
function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
    return typeof error === 'object' && error !== null && 'response' in error;
}

// Communication Service
export class CommunicationService {
    // ==================== TEMPLATES ====================

    async getTemplates(category?: string): Promise<EmailTemplate[]> {
        try {
            const params = category && category !== 'all' ? `?category=${category}` : '';
            const response = await api.get<ApiResponse<EmailTemplate[]>>(`${API_ENDPOINTS.templates}${params}`);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch templates');
            return response.data.data.map(this.transformTemplate);
        } catch (error) {
            console.error('Failed to fetch templates:', error);
            throw new Error('Failed to fetch templates. Please try again.');
        }
    }

    async getTemplateById(id: string): Promise<EmailTemplate | null> {
        try {
            const response = await api.get<ApiResponse<EmailTemplate>>(API_ENDPOINTS.template(id));
            if (!response.data.success) throw new Error(response.data.message || 'Template not found');
            return this.transformTemplate(response.data.data);
        } catch (error) {
            console.error('Failed to fetch template:', error);
            if (isErrorWithResponse(error) && error.response?.status === 404) return null;
            throw new Error('Failed to fetch template. Please try again.');
        }
    }

    async createTemplate(data: CreateEmailTemplateData): Promise<EmailTemplate> {
        try {
            const response = await api.post<ApiResponse<EmailTemplate>>(API_ENDPOINTS.templates, data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to create template');
            toast.success('Template created successfully');
            return this.transformTemplate(response.data.data);
        } catch (error) {
            console.error('Failed to create template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to create template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async updateTemplate(id: string, data: Partial<CreateEmailTemplateData>): Promise<EmailTemplate> {
        try {
            const response = await api.patch<ApiResponse<EmailTemplate>>(API_ENDPOINTS.template(id), data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update template');
            toast.success('Template updated successfully');
            return this.transformTemplate(response.data.data);
        } catch (error) {
            console.error('Failed to update template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to update template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async deleteTemplate(id: string): Promise<void> {
        try {
            const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.template(id));
            if (!response.data.success) throw new Error(response.data.message || 'Failed to delete template');
            toast.success('Template deleted successfully');
        } catch (error) {
            console.error('Failed to delete template:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to delete template. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== EMAIL LOGS ====================

    async getEmails(filters?: { searchQuery?: string; status?: string; partnershipId?: string }): Promise<EmailLog[]> {
        try {
            const params = new URLSearchParams();
            if (filters?.searchQuery) params.append('q', filters.searchQuery);
            if (filters?.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters?.partnershipId) params.append('partnershipId', filters.partnershipId);

            const queryString = params.toString();
            const url = queryString ? `${API_ENDPOINTS.emails}?${queryString}` : API_ENDPOINTS.emails;

            const response = await api.get<ApiResponse<EmailLog[]>>(url);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch emails');
            return response.data.data.map(this.transformEmail);
        } catch (error) {
            console.error('Failed to fetch emails:', error);
            throw new Error('Failed to fetch emails. Please try again.');
        }
    }

    async getEmailById(id: string): Promise<EmailLog | null> {
        try {
            const response = await api.get<ApiResponse<EmailLog>>(API_ENDPOINTS.email(id));
            if (!response.data.success) throw new Error(response.data.message || 'Email not found');
            return this.transformEmail(response.data.data);
        } catch (error) {
            console.error('Failed to fetch email:', error);
            if (isErrorWithResponse(error) && error.response?.status === 404) return null;
            throw new Error('Failed to fetch email. Please try again.');
        }
    }

    async sendEmail(data: CreateEmailLogData): Promise<EmailLog> {
        try {
            const response = await api.post<ApiResponse<EmailLog>>(API_ENDPOINTS.emails, data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to send email');
            toast.success('Email logged successfully');
            return this.transformEmail(response.data.data);
        } catch (error) {
            console.error('Failed to send email:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to send email. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async updateEmailStatus(id: string, status: EmailLog['status']): Promise<EmailLog> {
        try {
            const response = await api.patch<ApiResponse<EmailLog>>(API_ENDPOINTS.emailStatus(id), { status });
            if (!response.data.success) throw new Error(response.data.message || 'Failed to update status');
            toast.success('Email status updated');
            return this.transformEmail(response.data.data);
        } catch (error) {
            console.error('Failed to update email status:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to update status. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    // ==================== STATS ====================

    async getStats(): Promise<CommunicationStats> {
        try {
            const response = await api.get<ApiResponse<CommunicationStats>>(API_ENDPOINTS.stats);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch stats');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch stats:', error);
            throw new Error('Failed to fetch communication stats. Please try again.');
        }
    }

    // ==================== HELPERS ====================

    private transformTemplate(template: any): EmailTemplate {
        return {
            ...template,
            id: template.id || template._id,
            createdAt: template.createdAt || new Date().toISOString(),
            updatedAt: template.updatedAt || new Date().toISOString(),
            variables: Array.isArray(template.variables) ? template.variables : [],
            isActive: template.isActive !== false,
        };
    }

    private transformEmail(email: any): EmailLog {
        return {
            ...email,
            id: email.id || email._id,
            to: Array.isArray(email.to) ? email.to : [],
            cc: Array.isArray(email.cc) ? email.cc : [],
            template: email.templateId && typeof email.templateId === 'object'
                ? {
                    id: email.templateId.id || email.templateId._id,
                    name: email.templateId.name,
                    category: email.templateId.category,
                }
                : undefined,
            partnership: email.partnershipId && typeof email.partnershipId === 'object'
                ? {
                    id: email.partnershipId.id || email.partnershipId._id,
                    brand: email.partnershipId.brand,
                    contact: email.partnershipId.contact,
                    email: email.partnershipId.email,
                }
                : undefined,
            createdAt: email.createdAt || new Date().toISOString(),
            updatedAt: email.updatedAt || new Date().toISOString(),
            status: email.status || 'sent',
        };
    }

    // ==================== GMAIL INTEGRATION ====================

    async getGmailStatus(): Promise<{ connected: boolean; email?: string }> {
        try {
            const response = await api.get<ApiResponse<{ connected: boolean; email?: string }>>('/communication/gmail/status');
            if (!response.data.success) throw new Error(response.data.message || 'Failed to get Gmail status');
            return response.data.data;
        } catch (error) {
            console.error('Failed to get Gmail status:', error);
            return { connected: false };
        }
    }

    async getGmailAuthUrl(): Promise<string> {
        try {
            const response = await api.get<ApiResponse<{ authUrl: string }>>('/communication/gmail/auth-url');
            if (!response.data.success) throw new Error(response.data.message || 'Failed to get auth URL');
            return response.data.data.authUrl;
        } catch (error) {
            console.error('Failed to get Gmail auth URL:', error);
            throw new Error('Failed to connect Gmail. Please try again.');
        }
    }

    async getGmailEmails(maxResults: number = 20): Promise<GmailEmail[]> {
        try {
            const response = await api.get<ApiResponse<GmailEmail[]>>(`/communication/gmail/emails?maxResults=${maxResults}`);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch Gmail emails');
            return response.data.data;
        } catch (error) {
            console.error('Failed to fetch Gmail emails:', error);
            throw new Error('Failed to fetch Gmail emails. Please try again.');
        }
    }

    async sendViaGmail(data: { to: string[]; cc?: string[]; subject: string; bodyHtml: string; partnershipId?: string; templateId?: string }): Promise<{ messageId: string }> {
        try {
            const response = await api.post<ApiResponse<{ messageId: string }>>('/communication/gmail/send', data);
            if (!response.data.success) throw new Error(response.data.message || 'Failed to send email');
            toast.success('Email sent via Gmail');
            return response.data.data;
        } catch (error) {
            console.error('Failed to send via Gmail:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to send email. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }

    async disconnectGmail(): Promise<void> {
        try {
            const response = await api.delete<ApiResponse<void>>('/communication/gmail/disconnect');
            if (!response.data.success) throw new Error(response.data.message || 'Failed to disconnect Gmail');
            toast.success('Gmail disconnected successfully');
        } catch (error) {
            console.error('Failed to disconnect Gmail:', error);
            const message = isErrorWithResponse(error) && error.response?.data?.message
                ? error.response.data.message
                : 'Failed to disconnect Gmail. Please try again.';
            toast.error(message);
            throw new Error(message);
        }
    }
}

// Gmail Email interface
export interface GmailEmail {
    id: string;
    threadId: string;
    from: string;
    to: string[];
    subject: string;
    snippet: string;
    date: Date | string;
    isRead: boolean;
    labels: string[];
}

// Singleton instance
export const communicationService = new CommunicationService();

